import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { MoodboardItem } from '../types/moodboard';
interface LightboxProps {
  item: MoodboardItem | null;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}
export function Lightbox({
  item,
  onClose,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext
}: LightboxProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrevious) onPrevious();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrevious, onNext, hasPrevious, hasNext]);
  if (!item || item.type !== 'image') return null;
  return <AnimatePresence data-id="element-241">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.3
    }} className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4 sm:p-8" onClick={onClose} data-id="element-242">
        {/* Close Button */}
        <button onClick={onClose} className="absolute top-4 right-4 sm:top-8 sm:right-8 text-white/70 hover:text-white transition-colors z-10" aria-label="Close lightbox" data-id="element-243">
          <X className="w-8 h-8" strokeWidth={1.5} data-id="element-244" />
        </button>

        {/* Previous Button */}
        {hasPrevious && <button onClick={e => {
        e.stopPropagation();
        onPrevious();
      }} className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10" aria-label="Previous image" data-id="element-245">
            <ChevronLeft className="w-12 h-12" strokeWidth={1.5} data-id="element-246" />
          </button>}

        {/* Next Button */}
        {hasNext && <button onClick={e => {
        e.stopPropagation();
        onNext();
      }} className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10" aria-label="Next image" data-id="element-247">
            <ChevronRight className="w-12 h-12" strokeWidth={1.5} data-id="element-248" />
          </button>}

        {/* Content */}
        <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0.9,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="max-w-5xl w-full flex flex-col items-center" onClick={e => e.stopPropagation()} data-id="element-249">
          <img src={item.content} alt={item.title || 'Lightbox image'} className="w-full h-auto max-h-[70vh] object-contain" data-id="element-250" />

          {(item.title || item.description) && <div className="mt-6 sm:mt-8 text-center max-w-2xl" data-id="element-251">
              {item.title && <h3 className="font-heading font-normal text-[13px] sm:text-[14px] tracking-[0.25em] uppercase text-white mb-3" data-id="element-252">
                  {item.title}
                </h3>}
              {item.description && <p className="text-[13px] sm:text-[14px] text-white/80 leading-[1.7] font-sans" data-id="element-253">
                  {item.description}
                </p>}
            </div>}
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}